import { useState } from 'react';
import PageHero from '../components/PageHero.jsx';
import PersonCard from '../components/PersonCard.jsx';
import InfoIcon from '../components/InfoIcon.jsx';
import { PEOPLE, CONTACT_BY_ISSUE } from '../data/people.js';
import { SOURCES } from '../data/sources.js';
import './People.css';

const byGroup = group => PEOPLE.filter(p => p.group === group);

function telHref(phone) {
  return `tel:+1${phone.replace(/\D/g, '')}`;
}

export default function People() {
  const [copied, setCopied] = useState(null);

  function copyPhone(id, phone) {
    try {
      navigator.clipboard.writeText(phone);
    } catch {
      // Clipboard unavailable — tap target still links via tel:
    }
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div className="people">
      <PageHero icon="/assets/eagle/page-icons/people.png" title="People" />

      {/* ── Leadership ───────────────────────────────────────── */}
      <section className="people-section" aria-labelledby="leadership-heading">
        <h2 id="leadership-heading" className="people-section-title">
          Leadership
          <InfoIcon source={SOURCES.crmsCampusInfo} />
        </h2>
        <div className="people-grid">
          {byGroup('leadership').map(person => (
            <PersonCard key={person.id} person={person} />
          ))}
        </div>
      </section>

      {/* ── Counselors ───────────────────────────────────────── */}
      <section className="people-section" aria-labelledby="counselors-heading">
        <h2 id="counselors-heading" className="people-section-title">
          Counselors
          <InfoIcon source={SOURCES.crmsCounseling} />
        </h2>
        <p className="people-section-note">
          Your counselor is assigned by grade and last name — check the responsibility column below.
          Reach either counselor at{' '}
          <a href="mailto:counseling@leanderisd.org" className="people-inline-link">
            counseling@leanderisd.org
          </a>
          .
        </p>
        <div className="people-grid">
          {byGroup('counselors').map(person => (
            <PersonCard key={person.id} person={person} />
          ))}
        </div>
      </section>

      {/* ── Support Staff ────────────────────────────────────── */}
      <section className="people-section" aria-labelledby="support-heading">
        <h2 id="support-heading" className="people-section-title">
          Support Staff
          <InfoIcon source={SOURCES.crmsCampusInfo} />
        </h2>
        <div className="people-grid">
          {byGroup('support').map(person => (
            <PersonCard key={person.id} person={person} />
          ))}
        </div>
      </section>

      {/* ── Contact by Issue ─────────────────────────────────── */}
      <section className="people-section" aria-labelledby="contact-heading">
        <h2 id="contact-heading" className="people-section-title">
          Contact by issue
          <InfoIcon source={SOURCES.crmsCampusInfo} />
        </h2>
        <p className="people-section-note">
          Not sure who to call? Tap the number to dial — or copy it to your contacts.
        </p>

        <ul className="contact-list" role="list">
          {CONTACT_BY_ISSUE.map(row => (
            <li key={row.id} className="contact-row">
              <div className="contact-row-main">
                <span className="contact-issue">{row.issue}</span>
                <span className="contact-name">{row.contact}</span>
              </div>

              <div className="contact-row-links">
                {row.phone && (
                  <a href={telHref(row.phone)} className="contact-link contact-link--phone">
                    {row.phone}
                  </a>
                )}
                {row.email && (
                  <a href={`mailto:${row.email}`} className="contact-link contact-link--email">
                    {row.email}
                  </a>
                )}
                {row.url && (
                  <a
                    href={row.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-link contact-link--web"
                  >
                    website ↗
                  </a>
                )}
              </div>

              {row.phone && (
                <button
                  type="button"
                  className={`contact-copy-btn${copied === row.id ? ' contact-copy-btn--done' : ''}`}
                  onClick={() => copyPhone(row.id, row.phone)}
                  aria-label={`Copy phone number ${row.phone}`}
                >
                  {copied === row.id ? 'Copied!' : 'Copy #'}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
